import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, RefreshCw, Download, FileText, FileSpreadsheet, Clipboard } from 'lucide-react';
import { ColumnVisibilityToggle } from './ColumnVisibilityToggle';
import type { RoleColumnKey } from '../types';

interface RolesFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  onRefresh?: () => void;
  isFetching?: boolean;
  onExportCSV?: () => void;
  onExportExcel?: () => void;
  onCopyToClipboard?: () => void;
  visibleColumns?: RoleColumnKey[];
  onVisibleColumnsChange?: (columns: RoleColumnKey[]) => void;
}

export function RolesFilters({
  search,
  onSearchChange,
  pageSize,
  onPageSizeChange,
  onRefresh,
  isFetching,
  onExportCSV,
  onExportExcel,
  onCopyToClipboard,
  visibleColumns,
  onVisibleColumnsChange,
}: RolesFiltersProps) {
  const { t } = useTranslation('roles');

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search roles..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Page Size */}
      <Select
        value={String(pageSize)}
        onValueChange={(value) => onPageSizeChange(Number(value))}
      >
        <SelectTrigger className="w-[130px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="10">10 per page</SelectItem>
          <SelectItem value="25">25 per page</SelectItem>
          <SelectItem value="50">50 per page</SelectItem>
          <SelectItem value="100">100 per page</SelectItem>
        </SelectContent>
      </Select>

      {/* Refresh */}
      {onRefresh && (
        <Button
          variant="outline"
          size="icon"
          onClick={onRefresh}
          title={t('toolbar.refresh')}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      )}

      {/* Column Visibility */}
      {visibleColumns && onVisibleColumnsChange && (
        <ColumnVisibilityToggle
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={onVisibleColumnsChange}
        />
      )}

      {/* Export */}
      {(onExportCSV || onExportExcel || onCopyToClipboard) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" title={t('toolbar.export')}>
              <Download className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onExportCSV && (
              <DropdownMenuItem onClick={onExportCSV}>
                <FileText className="h-4 w-4 mr-2" />
                {t('toolbar.exportCSV')}
              </DropdownMenuItem>
            )}
            {onExportExcel && (
              <DropdownMenuItem onClick={onExportExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t('toolbar.exportExcel')}
              </DropdownMenuItem>
            )}
            {onCopyToClipboard && (
              <DropdownMenuItem onClick={onCopyToClipboard}>
                <Clipboard className="h-4 w-4 mr-2" />
                {t('toolbar.copyClipboard')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
